import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Scale, Ruler, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface BodyCompositionCardProps {
  records: any[];
  onRecordsChange: (updatedRecords: any[]) => void;
}

export function BodyCompositionCard({ records, onRecordsChange }: BodyCompositionCardProps) {
  const bodyRecords = records.filter(r =>
    r.type === 'body_composition' ||
    r.type === 'anthropometry' ||
    r.type === 'body_measurement'
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  if (bodyRecords.length === 0) return null;

  const isAnthropometry = bodyRecords.some(r => r.type === 'anthropometry' || r.type === 'body_measurement');

  const handleEdit = (idx: number) => {
    const originalIndex = records.indexOf(bodyRecords[idx]);
    setEditForm(records[originalIndex]);
    setEditingIndex(originalIndex);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...records];
    updated[editingIndex] = editForm;
    onRecordsChange(updated);
    setEditingIndex(null);
    toast.success("Registro atualizado!");
  };

  const handleDelete = (idx: number) => {
    const originalIndex = records.indexOf(bodyRecords[idx]);
    setDeleteIndex(originalIndex);
  };

  const confirmDelete = () => {
    if (deleteIndex === null) return;
    const updated = records.filter((_, i) => i !== deleteIndex);
    onRecordsChange(updated);
    setDeleteIndex(null);
    toast.success("Registro excluído!");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {isAnthropometry ? (
          <Ruler className="h-5 w-5 text-blue-600" />
        ) : (
          <Scale className="h-5 w-5 text-blue-600" />
        )}
        <h3 className="text-lg font-semibold text-blue-600">
          {isAnthropometry ? 'Antropometria' : 'Composição Corporal'}
        </h3>
        <Badge variant="secondary">{bodyRecords.length} medidas</Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {bodyRecords.map((record, idx) => (
          <Card key={idx} className="border-blue-600/20 relative">
            <CardContent className="pt-4">
              <div className="absolute top-2 right-2 flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEdit(idx)}
                  className="text-primary hover:bg-primary/10 h-7 w-7 p-0"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(idx)}
                  className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center pr-16">
                  <span className="text-xs font-medium text-muted-foreground uppercase">
                    {record.measurement?.replace(/_/g, ' ') || record.measurement_type?.replace(/_/g, ' ')}
                  </span>
                  {record.date && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(record.date).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </div>

                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-secondary">
                    {record.value}
                  </span>
                  {record.unit && (
                    <span className="text-sm text-muted-foreground">{record.unit}</span>
                  )}
                </div>

                {record.percentage != null && (
                  <div className="text-sm text-muted-foreground">
                    {record.percentage}%
                  </div>
                )}

                {record.classification && (
                  <Badge variant="secondary" className="text-xs">
                    {record.classification}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={editingIndex !== null} onOpenChange={() => setEditingIndex(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Editar Medida Corporal</AlertDialogTitle>
          </AlertDialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Tipo de Medição</Label>
              <Input
                value={editForm.measurement || editForm.measurement_type || ""}
                onChange={(e) => setEditForm({
                  ...editForm,
                  measurement: e.target.value,
                  measurement_type: e.target.value
                })}
                placeholder="Ex: peso, altura, gordura_corporal"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valor</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.value || ""}
                  onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                />
              </div>
              <div>
                <Label>Unidade</Label>
                <Input
                  value={editForm.unit || ""}
                  onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                  placeholder="Ex: kg, cm, %"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={editForm.date || ""}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Porcentagem (opcional)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.percentage || ""}
                onChange={(e) => setEditForm({ ...editForm, percentage: e.target.value })}
                placeholder="Ex: 15.5"
              />
            </div>

            <div className="grid gap-2">
              <Label>Classificação</Label>
              <Input
                value={editForm.classification || ""}
                onChange={(e) => setEditForm({ ...editForm, classification: e.target.value })}
                placeholder="Ex: Normal, Elevado, Baixo"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveEdit}>Salvar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteIndex !== null} onOpenChange={() => setDeleteIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}